import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import React from 'react'
import { useSelector } from 'react-redux'
import { z } from 'zod'

import Flag from '@/app/component/atom/flag'
import TabHeader from '@/app/component/atom/tab-header'
import { Form } from '@/app/component/form'
import SubmitButton from '@/app/component/form/submit-button'
import { isPhone } from '@/app/component/form/utils/validation'
import { useSaveUser } from '@/app/module/user/hooks/use-save-user'
import { selectUserDetails } from '@/app/module/user/store/selectors'
import { selectDefaultCountryCode } from '@/app/module/utils/store/selectors'
import { addPlusToPhoneNumber, trimPhoneNumberPlus } from '@/app/service/util/format'
import { esCountries } from '@/config/resources'

type CountryOption = {
  value: string
  label: string
  code: string
}

const ProfileInfoForm: React.FC = () => {
  const countryCode = useSelector(selectDefaultCountryCode)
  const user = useSelector(selectUserDetails) as any
  const [save, { isLoading: isSubmitting }] = useSaveUser()

  const defaultValues = React.useMemo<ProfileInfoData>(() => {
    return {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      country: user?.country || '',
      phone: user?.phone ? addPlusToPhoneNumber(user.phone) : '',
    } as ProfileInfoData
  }, [user?.firstName, user?.lastName, user?.country, user?.phone])

  const handleSubmit = React.useCallback(
    (data: ProfileInfoData) =>
      save({
        firstName: data.firstName,
        lastName: data.lastName,
        country: data.country || '',
        phone: trimPhoneNumberPlus(data.phone || ''),
      }),
    [save],
  )

  return (
    <Card>
      <TabHeader title="Profile" />
      <CardContent>
        <Form
          defaultValues={defaultValues}
          fields={{
            firstName: {
              type: 'text',
              fieldProps: {
                label: 'First name',
                name: 'firstName',
                required: true,
              },
            },
            lastName: {
              type: 'text',
              fieldProps: {
                label: 'Last name',
                name: 'lastName',
                required: true,
              },
            },
            country: {
              type: 'autocomplete',
              fieldProps: {
                label: 'Country',
                options: countryOptions,
                name: 'country',
                renderOption: (itemProps, option) => {
                  const { code, label } = option as CountryOption
                  return (
                    <Box component="li" sx={{ display: 'flex', gap: '0.5rem' }} {...itemProps}>
                      <Flag code={code} />
                      <span>{label}</span>
                    </Box>
                  )
                },
              },
            },
            phone: {
              type: 'tel',
              fieldProps: {
                label: 'Phone number',
                name: 'phone',
                defaultCountry: countryCode,
                required: true,
              },
            },
          }}
          schema={schema}
          shouldReset
          onSubmit={handleSubmit}
          renderActions={({ isDirty }) => (
            <CardActions sx={{ p: 0 }}>
              <SubmitButton isDirty={isDirty} isSubmitting={isSubmitting}>
                Save
              </SubmitButton>
            </CardActions>
          )}
        />
      </CardContent>
    </Card>
  )
}

const countryOptions: CountryOption[] = esCountries.map(({ name, code }) => ({ value: name, label: name, code }))

const schema = z.object({
  firstName: z.string().trim().min(1, { message: 'First name is required' }).describe('First Name'),
  lastName: z.string().trim().min(1, { message: 'Last name is required' }).describe('Last Name'),
  phone: z.string().min(1, { message: 'Phone number is required' }).describe('Phone number').transform(isPhone(true)),
  country: z.string().optional().describe('Country'),
})

type ProfileInfoData = z.infer<typeof schema>

export default ProfileInfoForm

export type { ProfileInfoData }
